/**
 * Parseo y validación del ABM de competencias (`PUT /api/competitions/:id`).
 *
 * POR QUÉ EXISTE
 * --------------
 * Crear una competencia —fases, grupos, sistema de puntos, desempates— era lo
 * único del ítem 1.2 que seguía siendo `curl`: el portal ya mostraba fixture,
 * tabla y ficha, y el cliente del API ya tenía `saveCompetition`, pero ninguna
 * pantalla lo llamaba. Si en la reunión piden *"cargámelo en vivo"*, esto es lo
 * que evita abrir la consola.
 *
 * CÓMO ESTÁ REPARTIDO
 * -------------------
 * Todo el parseo y la validación viven acá, puros y testeables sin DOM; el
 * componente sólo muestra errores (mismo criterio que `team-form.ts`). Lo que
 * es lista larga —equipos, grupos, tabla de puntos— se edita como TEXTO, una
 * línea por fila: cargar 16 clubes con inputs sueltos es un suplicio y la
 * federación ya tiene la lista escrita en algún lado para pegar.
 *
 * LO QUE ESTE FORMULARIO NO EDITA PERO TAMPOCO PUEDE PERDER
 * --------------------------------------------------------
 * El `PUT` es **entero**: lo que no se manda, se borra. Una competencia armada
 * por API puede traer cosas que esta pantalla todavía no edita —el sistema de
 * puntos propio de una fase, sus desempates propios, las quitas de puntos por
 * sanción (`adjustments`)—, así que el formulario las **transporta intactas**
 * (`PhasePreserved`) en vez de dejarlas caer en silencio. Perder una quita de
 * puntos por abrir y guardar una pantalla sería el peor bug posible de este
 * módulo: cambia una tabla de posiciones sin que nadie lo pida.
 *
 * PARIDAD CON EL CONTRATO
 * -----------------------
 * El portal consume JSON y **no ejecuta `@scout/core`** (ver `vite.config.ts`),
 * así que las listas cerradas del contrato están copiadas acá con su fuente
 * anotada. `test/competition-form.test.ts` las compara contra `@scout/core` y
 * falla si alguien agrega un criterio de desempate y se olvida del portal.
 */
import type {
  Competition,
  CompetitionGroup,
  CompetitionInput,
  CompetitionPhase,
  CompetitionStatus,
  CompetitionTeam,
  PhaseAdjustment,
  PhaseKind,
  ScoringSystem,
  TiebreakCriterion,
} from '@scout/core';
import { getT, type MessageKey, type TFunction } from '../i18n/index';
// Los ids del sistema (equipo, competencia, fase, grupo) comparten patrón, así
// que la propuesta de id es la MISMA función que la de equipos y no una copia
// que se pueda desincronizar.
import { slugifyTeamId } from './team-form';

export { slugifyTeamId as slugifyId };

// ---------------------------------------------------------------------------
// Listas cerradas del contrato (copiadas: ver "PARIDAD" arriba)
// ---------------------------------------------------------------------------

/** `COMPETITION_STATUSES` de `packages/core/src/contracts/competition.ts`. */
export const STATUSES: readonly CompetitionStatus[] = ['draft', 'active', 'finished'];

/** `PHASE_KINDS`. `regular` y `groups` tienen tabla; `playoff` tiene llave. */
export const PHASE_KINDS: readonly PhaseKind[] = ['regular', 'groups', 'playoff'];

/** `TIEBREAK_CRITERIA`. El ORDEN de la lista guardada es el desempate. */
export const TIEBREAK_CRITERIA: readonly TiebreakCriterion[] = [
  'points',
  'matchesWon',
  'setRatio',
  'setDiff',
  'pointRatio',
  'pointDiff',
  'headToHead',
  'setsWon',
  'pointsScored',
];

/** `DEFAULT_TIEBREAKERS`: el orden FIVB, que es lo que espera la federación. */
export const DEFAULT_TIEBREAKERS: readonly TiebreakCriterion[] = [
  'points',
  'matchesWon',
  'setRatio',
  'pointRatio',
  'headToHead',
];

/** `SCORING_PRESET_IDS`. Son puntos de partida, no la lista cerrada. */
export const SCORING_PRESET_IDS = ['fivb', 'fivb-bo3', 'win-loss-2-1', 'win-1-0'] as const;
export type ScoringPresetId = (typeof SCORING_PRESET_IDS)[number];

/**
 * `SPORT_PRESETS` de `contracts/sport.ts`. Hoy el registry es cerrado (el API
 * responde 422 `unknown_sport_config` para cualquier otro id), pero el
 * formulario conserva el valor que traiga la ficha aunque no esté en la lista:
 * el día que el ruleset sea persistido (`docs/acta-electronica.md` §7.2) esta
 * pantalla no tiene que cambiar para no romper lo que ya existía.
 */
export const SPORT_CONFIG_IDS = ['volleyball-indoor', 'volleyball-beach'] as const;

export function sportConfigOptions(current: string): string[] {
  const ids: string[] = [...SPORT_CONFIG_IDS];
  return ids.includes(current) || current.trim() === '' ? ids : [...ids, current];
}

// ---------------------------------------------------------------------------
// Sistema de puntos
// ---------------------------------------------------------------------------

/**
 * Los presets, copiados de `SCORING_PRESETS`. La etiqueta NO se copia del
 * contrato: ahí está en castellano y acá tiene que seguir al idioma de la
 * interfaz, así que el formulario la arma con `t()` al aplicar el preset.
 */
export const SCORING_PRESETS: Record<ScoringPresetId, ScoringSystem> = {
  fivb: {
    rules: [
      { winnerSets: 3, loserSets: 0, winnerPoints: 3, loserPoints: 0 },
      { winnerSets: 3, loserSets: 1, winnerPoints: 3, loserPoints: 0 },
      { winnerSets: 3, loserSets: 2, winnerPoints: 2, loserPoints: 1 },
    ],
    winPoints: 3,
    lossPoints: 0,
    forfeit: { winnerSets: 3, loserSets: 0, pointsPerSet: 25, loserPoints: 0 },
  },
  'fivb-bo3': {
    rules: [
      { winnerSets: 2, loserSets: 0, winnerPoints: 3, loserPoints: 0 },
      { winnerSets: 2, loserSets: 1, winnerPoints: 2, loserPoints: 1 },
    ],
    winPoints: 3,
    lossPoints: 0,
    forfeit: { winnerSets: 2, loserSets: 0, pointsPerSet: 25, loserPoints: 0 },
  },
  'win-loss-2-1': {
    rules: [],
    winPoints: 2,
    lossPoints: 1,
    forfeit: { winnerSets: 3, loserSets: 0, pointsPerSet: 25, loserPoints: 0 },
  },
  'win-1-0': {
    rules: [],
    winPoints: 1,
    lossPoints: 0,
    forfeit: { winnerSets: 3, loserSets: 0, pointsPerSet: 25, loserPoints: 0 },
  },
};

export interface ScoringFormValues {
  /** `custom` = la tabla se editó a mano y ya no es ninguno de los presets. */
  presetId: ScoringPresetId | 'custom';
  label: string;
  /** Una línea por fila: `3-2 2 1` (sets del ganador-del perdedor, puntos). */
  rulesText: string;
  winPoints: string;
  lossPoints: string;
  forfeitWinnerSets: string;
  forfeitLoserSets: string;
  forfeitPointsPerSet: string;
  /** Vacío = el ganador cobra lo que diga la tabla para ese resultado. */
  forfeitWinnerPoints: string;
  forfeitLoserPoints: string;
}

/** `ScoringSystem` (del API o de un preset) → los campos del formulario. */
export function scoringToForm(
  scoring: ScoringSystem,
  presetId: ScoringPresetId | 'custom' = 'custom',
): ScoringFormValues {
  return {
    presetId,
    label: scoring.label ?? '',
    rulesText: scoringRulesToText(scoring.rules),
    winPoints: String(scoring.winPoints),
    lossPoints: String(scoring.lossPoints),
    forfeitWinnerSets: String(scoring.forfeit.winnerSets),
    forfeitLoserSets: String(scoring.forfeit.loserSets),
    forfeitPointsPerSet: String(scoring.forfeit.pointsPerSet),
    forfeitWinnerPoints:
      scoring.forfeit.winnerPoints === undefined ? '' : String(scoring.forfeit.winnerPoints),
    forfeitLoserPoints: String(scoring.forfeit.loserPoints),
  };
}

/**
 * ¿Este sistema de puntos es uno de los presets? Se compara todo menos la
 * etiqueta (que es texto libre y sigue al idioma).
 *
 * Existe para que abrir la ficha de una liga que usa la tabla FIVB diga
 * **FIVB** en el selector y no "Personalizado": el selector tiene que describir
 * lo que está guardado, no lo que la pantalla sabe de dónde salió.
 */
export function detectScoringPreset(scoring: ScoringSystem): ScoringPresetId | 'custom' {
  for (const id of SCORING_PRESET_IDS) {
    const preset = SCORING_PRESETS[id];
    if (
      preset.winPoints === scoring.winPoints &&
      preset.lossPoints === scoring.lossPoints &&
      mismasReglas(preset.rules, scoring.rules) &&
      mismoForfeit(preset.forfeit, scoring.forfeit)
    ) {
      return id;
    }
  }
  return 'custom';
}

function mismasReglas(a: ScoringSystem['rules'], b: ScoringSystem['rules']): boolean {
  return (
    a.length === b.length &&
    a.every((rule, i) => {
      const otra = b[i];
      return (
        otra !== undefined &&
        rule.winnerSets === otra.winnerSets &&
        rule.loserSets === otra.loserSets &&
        rule.winnerPoints === otra.winnerPoints &&
        rule.loserPoints === otra.loserPoints
      );
    })
  );
}

function mismoForfeit(a: ScoringSystem['forfeit'], b: ScoringSystem['forfeit']): boolean {
  return (
    a.winnerSets === b.winnerSets &&
    a.loserSets === b.loserSets &&
    a.pointsPerSet === b.pointsPerSet &&
    a.loserPoints === b.loserPoints &&
    a.winnerPoints === b.winnerPoints
  );
}

/** Etiqueta visible de cada preset. En el contrato está sólo en castellano. */
export function scoringPresetLabelKey(presetId: ScoringPresetId): MessageKey {
  switch (presetId) {
    case 'fivb-bo3':
      return 'competitionForm.presetFivbBo3';
    case 'win-loss-2-1':
      return 'competitionForm.presetWinLoss21';
    case 'win-1-0':
      return 'competitionForm.presetWin10';
    default:
      return 'competitionForm.presetFivb';
  }
}

/** El preset elegido, con su etiqueta en el idioma de la interfaz. */
export function applyScoringPreset(
  presetId: ScoringPresetId,
  t: TFunction = getT(),
): ScoringFormValues {
  return {
    ...scoringToForm(SCORING_PRESETS[presetId], presetId),
    label: t(scoringPresetLabelKey(presetId)),
  };
}

export function scoringRulesToText(rules: ScoringSystem['rules']): string {
  return rules
    .map((rule) => `${rule.winnerSets}-${rule.loserSets} ${rule.winnerPoints} ${rule.loserPoints}`)
    .join('\n');
}

export interface ParsedScoringRules {
  rules: ScoringSystem['rules'];
  errors: string[];
}

/**
 * Una línea por fila del reglamento. Se leen los NÚMEROS de la línea y tienen
 * que ser cuatro: sets del ganador, sets del perdedor, puntos del ganador,
 * puntos del perdedor. Así `3-2 2 1`, `3-2 → 2 y 1` y `3-2: 2, 1` valen lo
 * mismo — es texto que alguien copia de un reglamento en PDF, no un CSV.
 */
export function parseScoringRulesText(
  text: string,
  t: TFunction = getT(),
): ParsedScoringRules {
  const rules: ScoringSystem['rules'] = [];
  const errors: string[] = [];
  const vistos = new Set<string>();

  text.split('\n').forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (line === '') return;
    const lineNo = index + 1;

    const numbers = (line.match(/\d+(?:[.,]\d+)?/g) ?? []).map((n) => Number(n.replace(',', '.')));
    if (numbers.length !== 4) {
      errors.push(t('competitionForm.errors.ruleShape', { line: lineNo }));
      return;
    }
    const [winnerSets, loserSets, winnerPoints, loserPoints] = numbers as [
      number,
      number,
      number,
      number,
    ];

    if (!Number.isInteger(winnerSets) || !Number.isInteger(loserSets) || winnerSets > 9 || loserSets > 9) {
      errors.push(t('competitionForm.errors.ruleSets', { line: lineNo }));
      return;
    }
    // Las filas se declaran DESDE LA PERSPECTIVA DEL GANADOR (contrato): así no
    // hay que escribir la fila espejo y no puede haber dos que se contradigan.
    if (winnerSets <= loserSets) {
      errors.push(t('competitionForm.errors.ruleWinner', { line: lineNo }));
      return;
    }
    if (winnerPoints > 99 || loserPoints > 99) {
      errors.push(t('competitionForm.errors.rulePoints', { line: lineNo }));
      return;
    }
    const key = `${winnerSets}-${loserSets}`;
    if (vistos.has(key)) {
      errors.push(t('competitionForm.errors.ruleRepeated', { line: lineNo, result: key }));
      return;
    }
    vistos.add(key);
    rules.push({ winnerSets, loserSets, winnerPoints, loserPoints });
  });

  return { rules, errors };
}

// ---------------------------------------------------------------------------
// Equipos y grupos
// ---------------------------------------------------------------------------

/** `bolivar, Club Bolívar, 1` → id, nombre y siembra (los dos últimos, opcionales). */
export function teamsToText(teams: readonly CompetitionTeam[]): string {
  return teams
    .map((team) => [team.teamId, team.name, team.seed === undefined ? '' : String(team.seed)]
      .join(', ')
      .replace(/,\s*$/, ''))
    .join('\n');
}

export interface ParsedTeams {
  teams: CompetitionTeam[];
  errors: string[];
}

/**
 * Una línea por club, en columnas separadas por coma: `id, Nombre, siembra`.
 *
 * Con una sola columna se toma como NOMBRE y el id se propone solo
 * (`Club Bolívar` → `club-bolivar`): es lo que hace falta para cargar una
 * competencia en vivo sin pensar en ids. Con dos o más, la primera es el id
 * —que es lo que engancha con la base de equipos y con el fixture ya generado—
 * y por eso nunca se adivina cuando la persona lo escribió.
 *
 * La siembra es la última columna **sólo si es un número**; si no, todo lo que
 * sigue al id es el nombre. Así un club con coma en el nombre (`ciudad, Ciudad
 * de Buenos Aires, Núñez`) se carga entero en vez de romperse en la línea que
 * más caro sale: la de la lista de participantes en la demo.
 */
export function parseTeamsText(text: string, t: TFunction = getT()): ParsedTeams {
  const teams: CompetitionTeam[] = [];
  const errors: string[] = [];
  const vistos = new Set<string>();

  text.split('\n').forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (line === '') return;
    const lineNo = index + 1;

    const cols = line.split(',').map((c) => c.trim());
    const ultima = cols[cols.length - 1] ?? '';
    const rawSeed = cols.length > 2 && /^#?\d+$/.test(ultima) ? (cols.pop() ?? '') : '';
    const first = cols[0] ?? '';
    const soloNombre = cols.length === 1;
    // Con una sola columna, esa columna es el NOMBRE y el id se propone.
    // Con más, la primera es el id; el nombre cae al id si vino vacío
    // (`bolivar,` es un tipeo a medias, no una competencia sin nombre de club).
    const teamId = soloNombre ? slugifyTeamId(first) : first;
    const name = soloNombre ? first : cols.slice(1).join(', ') || first;

    if (teamId === '') {
      errors.push(t('competitionForm.errors.teamId', { line: lineNo }));
      return;
    }
    if (!isValidId(teamId)) {
      errors.push(t('competitionForm.errors.teamIdInvalid', { line: lineNo, id: teamId }));
      return;
    }
    if (vistos.has(teamId)) {
      errors.push(t('competitionForm.errors.teamRepeated', { line: lineNo, id: teamId }));
      return;
    }
    if (name === '') {
      errors.push(t('competitionForm.errors.teamName', { line: lineNo }));
      return;
    }

    let seed: number | undefined;
    if (rawSeed !== '') {
      const parsed = Number(rawSeed.replace(/^#/, ''));
      if (!Number.isInteger(parsed) || parsed < 1 || parsed > 256) {
        errors.push(t('competitionForm.errors.teamSeed', { line: lineNo, token: rawSeed }));
        return;
      }
      seed = parsed;
    }

    vistos.add(teamId);
    teams.push({ teamId, name, ...(seed !== undefined ? { seed } : {}) });
  });

  return { teams, errors };
}

/** `Grupo A: bolivar, upcn` por línea. */
export function groupsToText(groups: readonly CompetitionGroup[]): string {
  return groups.map((group) => `${group.name}: ${group.teamIds.join(', ')}`).join('\n');
}

export interface ParsedGroups {
  groups: CompetitionGroup[];
  errors: string[];
}

/**
 * `Nombre del grupo: id, id, id`.
 *
 * El id del grupo NO se escribe: sale del nombre. Pero si el grupo ya existía
 * con ese mismo nombre, **se conserva su id original** (`previous`), porque el
 * fixture ya generado apunta a él: recalcularlo convertiría "Grupo A" en
 * `grupo-a` y dejaría huérfanos todos los partidos que decían `a`.
 */
export function parseGroupsText(
  text: string,
  previous: readonly CompetitionGroup[] = [],
  t: TFunction = getT(),
): ParsedGroups {
  const groups: CompetitionGroup[] = [];
  const errors: string[] = [];
  const vistos = new Set<string>();
  const porNombre = new Map(previous.map((g) => [g.name.trim().toLowerCase(), g.id]));

  text.split('\n').forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (line === '') return;
    const lineNo = index + 1;

    const colon = line.indexOf(':');
    const name = (colon === -1 ? line : line.slice(0, colon)).trim();
    const rawTeams = colon === -1 ? '' : line.slice(colon + 1);

    if (name === '') {
      errors.push(t('competitionForm.errors.groupName', { line: lineNo }));
      return;
    }
    const id = porNombre.get(name.toLowerCase()) ?? slugifyTeamId(name);
    if (id === '' || !isValidId(id)) {
      errors.push(t('competitionForm.errors.groupId', { line: lineNo, name }));
      return;
    }
    if (vistos.has(id)) {
      errors.push(t('competitionForm.errors.groupRepeated', { line: lineNo, name }));
      return;
    }
    vistos.add(id);

    const teamIds: string[] = [];
    for (const raw of rawTeams.split(',')) {
      const teamId = raw.trim();
      if (teamId === '') continue;
      if (!teamIds.includes(teamId)) teamIds.push(teamId);
    }
    groups.push({ id, name, teamIds });
  });

  return { groups, errors };
}

/** `bolivar, upcn` o una lista con saltos de línea → ids, sin repetidos. */
export function parseIdList(text: string): string[] {
  const ids: string[] = [];
  for (const raw of text.split(/[\n,]/)) {
    const id = raw.trim();
    if (id !== '' && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

// ---------------------------------------------------------------------------
// Fases
// ---------------------------------------------------------------------------

/**
 * Lo que la fase trae del API y esta pantalla todavía no edita. Viaja de ida y
 * de vuelta sin tocarse: ver la nota de arriba sobre el `PUT` entero.
 */
export interface PhasePreserved {
  scoring?: ScoringSystem;
  tiebreakers?: TiebreakCriterion[];
  adjustments: PhaseAdjustment[];
}

export interface PhaseFormValues {
  id: string;
  name: string;
  kind: PhaseKind;
  legs: 1 | 2;
  /** Ids de equipo (en `groups` manda lo que digan los grupos). */
  teamsText: string;
  groupsText: string;
  /** Grupos como venían, para poder conservarles el id al reescribir. */
  previousGroups: CompetitionGroup[];
  /**
   * Vino del servidor: el id queda bloqueado. Cambiarlo desengancharía el
   * fixture y la tabla de esa fase, que es justo lo que no se puede hacer a
   * mitad de temporada.
   */
  existing: boolean;
  preserved: PhasePreserved;
}

export function emptyPhase(): PhaseFormValues {
  return {
    id: '',
    name: '',
    kind: 'regular',
    legs: 1,
    teamsText: '',
    groupsText: '',
    previousGroups: [],
    existing: false,
    preserved: { adjustments: [] },
  };
}

export function phaseToForm(phase: CompetitionPhase): PhaseFormValues {
  return {
    id: phase.id,
    name: phase.name,
    kind: phase.kind,
    legs: phase.legs === 2 ? 2 : 1,
    teamsText: phase.teamIds.join(', '),
    groupsText: groupsToText(phase.groups),
    previousGroups: [...phase.groups],
    existing: true,
    preserved: {
      ...(phase.scoring !== undefined ? { scoring: phase.scoring } : {}),
      ...(phase.tiebreakers !== undefined ? { tiebreakers: [...phase.tiebreakers] } : {}),
      adjustments: [...phase.adjustments],
    },
  };
}

// ---------------------------------------------------------------------------
// El formulario completo
// ---------------------------------------------------------------------------

export interface CompetitionFormValues {
  competitionId: string;
  name: string;
  season: string;
  sportConfigId: string;
  description: string;
  status: CompetitionStatus;
  teamsText: string;
  scoring: ScoringFormValues;
  tiebreakers: TiebreakCriterion[];
  phases: PhaseFormValues[];
}

export function emptyCompetitionForm(t: TFunction = getT()): CompetitionFormValues {
  return {
    competitionId: '',
    name: '',
    season: '',
    sportConfigId: 'volleyball-indoor',
    description: '',
    status: 'draft',
    teamsText: '',
    // Los defaults son los que espera la federación si nadie dice otra cosa:
    // tabla FIVB y desempates en el orden FIVB. Una competencia nueva sale
    // bien configurada aunque la persona no toque nada de esta sección.
    scoring: applyScoringPreset('fivb', t),
    tiebreakers: [...DEFAULT_TIEBREAKERS],
    phases: [],
  };
}

export function competitionToForm(competition: Competition): CompetitionFormValues {
  return {
    competitionId: competition.id,
    name: competition.name,
    season: competition.season,
    sportConfigId: competition.sportConfigId,
    description: competition.description ?? '',
    status: competition.status,
    teamsText: teamsToText(competition.teams),
    scoring: scoringToForm(competition.scoring, detectScoringPreset(competition.scoring)),
    tiebreakers: [...competition.tiebreakers],
    phases: [...competition.phases]
      .sort((a, b) => a.order - b.order)
      .map((phase) => phaseToForm(phase)),
  };
}

export type CompetitionFormResult =
  | { ok: true; competitionId: string; input: CompetitionInput }
  | { ok: false; errors: string[] };

/**
 * Valida el formulario entero y arma el cuerpo del `PUT`.
 *
 * Repite los chequeos estructurales que el API ya hace (`validateStructure` en
 * `apps/api/src/routes/competitions.ts`) a propósito: el API sigue siendo la
 * autoridad, pero decirle a alguien que la fase menciona un equipo que no
 * cargó no vale un request, y menos en el medio de una demo.
 */
export function buildCompetitionPayload(
  values: CompetitionFormValues,
  t: TFunction = getT(),
): CompetitionFormResult {
  const errors: string[] = [];
  const competitionId = values.competitionId.trim();
  const name = values.name.trim();

  if (competitionId === '') errors.push(t('competitionForm.errors.idRequired'));
  else if (!isValidId(competitionId)) errors.push(t('competitionForm.errors.idInvalid'));
  if (name === '') errors.push(t('competitionForm.errors.nameRequired'));
  if (values.season.trim().length > 40) errors.push(t('competitionForm.errors.seasonTooLong'));
  if (values.description.trim().length > 1000) {
    errors.push(t('competitionForm.errors.descriptionTooLong'));
  }

  const teams = parseTeamsText(values.teamsText, t);
  errors.push(...teams.errors);
  const teamIds = new Set(teams.teams.map((team) => team.teamId));

  const scoring = parseScoringRulesText(values.scoring.rulesText, t);
  errors.push(...scoring.errors);
  const winPoints = parsePoints(values.scoring.winPoints, 'competitionForm.winPoints', errors, t);
  const lossPoints = parsePoints(values.scoring.lossPoints, 'competitionForm.lossPoints', errors, t);
  const forfeitWinnerSets = parseCount(
    values.scoring.forfeitWinnerSets,
    'competitionForm.forfeitSets',
    errors,
    t,
    { min: 1, max: 9 },
  );
  const forfeitLoserSets = parseCount(
    values.scoring.forfeitLoserSets,
    'competitionForm.forfeitLoserSets',
    errors,
    t,
    { min: 0, max: 9 },
  );
  const forfeitPointsPerSet = parseCount(
    values.scoring.forfeitPointsPerSet,
    'competitionForm.forfeitPointsPerSet',
    errors,
    t,
    { min: 0, max: 99 },
  );
  const forfeitLoserPoints = parsePoints(
    values.scoring.forfeitLoserPoints,
    'competitionForm.forfeitLoserPoints',
    errors,
    t,
  );
  const forfeitWinnerPoints =
    values.scoring.forfeitWinnerPoints.trim() === ''
      ? undefined
      : parsePoints(values.scoring.forfeitWinnerPoints, 'competitionForm.forfeitWinnerPoints', errors, t);

  if (values.tiebreakers.length === 0) errors.push(t('competitionForm.errors.tiebreakEmpty'));

  const phases: CompetitionPhase[] = [];
  const phaseIds = new Set<string>();
  values.phases.forEach((phase, index) => {
    const id = phase.id.trim();
    const phaseName = phase.name.trim();
    const etiqueta = phaseName === '' ? String(index + 1) : phaseName;

    if (id === '') {
      errors.push(t('competitionForm.errors.phaseId', { phase: etiqueta }));
      return;
    }
    if (!isValidId(id)) {
      errors.push(t('competitionForm.errors.phaseIdInvalid', { phase: etiqueta }));
      return;
    }
    if (phaseIds.has(id)) {
      errors.push(t('competitionForm.errors.phaseRepeated', { id }));
      return;
    }
    phaseIds.add(id);
    if (phaseName === '') {
      errors.push(t('competitionForm.errors.phaseName', { id }));
      return;
    }

    const groups = parseGroupsText(phase.groupsText, phase.previousGroups, t);
    errors.push(...groups.errors);
    const phaseTeamIds = parseIdList(phase.teamsText);

    // Un equipo que la fase menciona y la competencia no tiene produce tablas
    // con un id crudo donde debería ir el nombre del club: se corta antes.
    for (const teamId of [...phaseTeamIds, ...groups.groups.flatMap((g) => g.teamIds)]) {
      if (!teamIds.has(teamId)) {
        errors.push(t('competitionForm.errors.phaseUnknownTeam', { phase: etiqueta, id: teamId }));
      }
    }

    phases.push({
      id,
      name: phaseName,
      kind: phase.kind,
      // El orden de la ficha es el orden en que están en la pantalla.
      order: index,
      teamIds: phaseTeamIds,
      groups: groups.groups,
      legs: phase.legs,
      ...(phase.preserved.scoring !== undefined ? { scoring: phase.preserved.scoring } : {}),
      ...(phase.preserved.tiebreakers !== undefined
        ? { tiebreakers: phase.preserved.tiebreakers }
        : {}),
      adjustments: phase.preserved.adjustments,
    });
  });

  if (errors.length > 0) return { ok: false, errors };

  const label = values.scoring.label.trim();
  const description = values.description.trim();
  return {
    ok: true,
    competitionId,
    input: {
      name,
      season: values.season.trim(),
      sportConfigId: values.sportConfigId.trim() || 'volleyball-indoor',
      ...(description !== '' ? { description } : {}),
      status: values.status,
      teams: teams.teams,
      scoring: {
        ...(label !== '' ? { label } : {}),
        rules: scoring.rules,
        winPoints,
        lossPoints,
        forfeit: {
          winnerSets: forfeitWinnerSets,
          loserSets: forfeitLoserSets,
          pointsPerSet: forfeitPointsPerSet,
          ...(forfeitWinnerPoints !== undefined ? { winnerPoints: forfeitWinnerPoints } : {}),
          loserPoints: forfeitLoserPoints,
        },
      },
      tiebreakers: [...values.tiebreakers],
      phases,
    },
  };
}

// ---------------------------------------------------------------------------
// Desempates: la lista es ordenada, así que se mueve, no se marca
// ---------------------------------------------------------------------------

/** Sube (o baja) un criterio una posición. Fuera de rango, no hace nada. */
export function moveTiebreaker(
  criteria: readonly TiebreakCriterion[],
  index: number,
  delta: -1 | 1,
): TiebreakCriterion[] {
  const target = index + delta;
  if (index < 0 || index >= criteria.length || target < 0 || target >= criteria.length) {
    return [...criteria];
  }
  const next = [...criteria];
  const [moved] = next.splice(index, 1);
  if (moved !== undefined) next.splice(target, 0, moved);
  return next;
}

export function removeTiebreaker(
  criteria: readonly TiebreakCriterion[],
  criterion: TiebreakCriterion,
): TiebreakCriterion[] {
  return criteria.filter((c) => c !== criterion);
}

export function addTiebreaker(
  criteria: readonly TiebreakCriterion[],
  criterion: TiebreakCriterion,
): TiebreakCriterion[] {
  return criteria.includes(criterion) ? [...criteria] : [...criteria, criterion];
}

/** Los que todavía no están en la lista (lo que ofrece el selector). */
export function availableTiebreakers(
  criteria: readonly TiebreakCriterion[],
): TiebreakCriterion[] {
  return TIEBREAK_CRITERIA.filter((c) => !criteria.includes(c));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Mismo patrón que `COMPETITION_ID_PATTERN`: va en la URL y en disco. */
export function isValidId(id: string): boolean {
  return /^[A-Za-z0-9._-]{1,64}$/.test(id) && id !== '.' && id !== '..';
}

/** Puntos de tabla: admiten decimales (hay ligas que reparten medio punto). */
function parsePoints(raw: string, field: MessageKey, errors: string[], t: TFunction): number {
  const value = Number(raw.trim().replace(',', '.'));
  if (raw.trim() === '' || Number.isNaN(value) || value < 0 || value > 99) {
    errors.push(t('competitionForm.errors.number', { field: t(field) }));
    return 0;
  }
  return value;
}

/** Cantidades enteras (sets, parciales del W.O.). */
function parseCount(
  raw: string,
  field: MessageKey,
  errors: string[],
  t: TFunction,
  range: { min: number; max: number },
): number {
  const value = Number(raw.trim());
  if (raw.trim() === '' || !Number.isInteger(value) || value < range.min || value > range.max) {
    errors.push(t('competitionForm.errors.number', { field: t(field) }));
    return range.min;
  }
  return value;
}
